import React, { useContext, useEffect, useState } from "react";
import Router from "./router";
import "./App.css";
import { LogtoProvider } from "@logto/react";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { CloseIcon, ErrorFallbackComponent } from "components";
import { MainWrapper } from "layouts";
import toast, { Toast, ToastBar, Toaster } from "react-hot-toast";
import { mixpanel, Sentry } from "utilz";
import { convertStringToSentenceCase } from "utilz/helpers";
import { logtoConfig } from "utilz/logto";
import { ThemeProvider } from "utilz/theme/ThemeProvider";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // default: true
      retry: (failureCount, error: any) => {
        // Don't retry on 404 errors after first attempt
        if (error?.response?.status === 404) {
          return failureCount < 1;
        }
        // For other errors, retry up to 2 times
        return failureCount < 2;
      },
    },
  },
});

function App() {
  return (
    <LogtoProvider config={logtoConfig}>
      <ThemeProvider>
        <React.Fragment>
          <>
            <QueryClientProvider client={queryClient}>
              <div
                className="app"
                style={{
                  position: "relative",
                  minWidth: "100vw",
                  minHeight: "100vh",
                }}
              >
                <Sentry.ErrorBoundary
                  fallback={({ error }) => (
                    <ErrorFallbackComponent error={error} />
                  )}
                >
                  <Router />
                </Sentry.ErrorBoundary>
              </div>
              <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  backgroundColor: "#10172E",
                  borderRadius: "100px",
                  minWidth: "300px",
                  maxWidth: "500px",
                  color: "#FFF",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between !important",
                  minHeight: "48px",
                },
                success: {
                  duration: 3000,
                  style: {
                    border: "1px solid #00722A",
                  },
                },
                error: {
                  duration: 3000,
                  style: {
                    border: "1px solid #CE293A",
                  },
                },
                custom: {
                  duration: 3000,

                  style: {
                    backgroundColor: "#10172E",
                    border: "1px solid #F3C423",
                  },
                },
              }}
              // eslint-disable-next-line react/no-children-prop
              children={(t) => (
                <ToastBar toast={t}>
                  {({ icon, message }) => (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                        borderRadius: "24px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                        }}
                      >
                        <div aria-label="Close Notification">{icon}</div>
                        {message && React.isValidElement(message)
                          ? (() => {
                              const element = message as React.ReactElement<{
                                children?: React.ReactNode;
                              }>;
                              const newChildren =
                                typeof element.props.children === "string"
                                  ? convertStringToSentenceCase(
                                      element.props.children,
                                    )
                                  : element.props.children;

                              return React.cloneElement(
                                element,
                                element.props,
                                newChildren,
                              );
                            })()
                          : message}
                      </div>
                      <CloseIcon
                        style={{ cursor: "pointer" }}
                        onClick={() => toast.dismiss(t.id)}
                        htmlColor="#A5A5A6"
                      />
                    </div>
                  )}
                </ToastBar>
              )}
            ></Toaster>
          </>
        </React.Fragment>
      </ThemeProvider>
    </LogtoProvider>
  );
}

export default App;
